import { useSectionAccess } from "../auth/useSectionAccess";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { formatDate, formatDateTime, formatMoney, formatNumber } from "../lib/format";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { catalogApi, type ProductSummary } from "../api/catalog";
import { offersApi, type CustomerSuggestion, type DinarRate, type Offer, type OfferDocLanguage } from "../api/offers";
import { ordersApi } from "../api/orders";
import { contractsApi, type Contract } from "../api/contracts";
import type { ContractErpTerms } from "../api/contracts";
import { offerStagesApi, type OfferStage } from "../api/offerStages";
import { madeOrderNumbers, toOrderAction, toOrderClickable, type MadeOrder } from "./offerToOrder";
import {
  GENERAL_VAT, contractPriceIn, isDomestic, isPieces, offerCurrencyForCountry, offerLineFromProduct, offerVat,
  type OfferEditLine,
} from "./offerLine";
import { offerPaymentFromErp } from "./offerPayment";
import { useConfirm } from "../components/ConfirmDialog";
import { OfferPipelineHeader } from "../components/offers/OfferPipelineHeader";
import { ErpReadOnlyNote } from "../components/ErpReadOnlyNote";
import { SendMailModal } from "../components/mail/SendMailModal";
import { mailApi, type Communication } from "../api/mail";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/Toast";
import { Select } from "../components/Select";
import { CountrySelect } from "../components/CountrySelect";
import { SUPPORTED_LOCALES } from "../i18n";
import { localeFlag, localeLabel } from "../i18n/LocaleSwitcher";
import Ic from "../components/icons.jsx";
import { QtyStepper } from "../components/QtyStepper";
import type { ApiError } from "../api/client";

type EditItem = OfferEditLine;

const toEditItems = (o: Offer): EditItem[] => o.items.map((i) => ({
  productId: i.productId, sku: i.sku, name: i.name, quantity: Number(i.quantity),
  unit: i.unit, unitPrice: i.unitPrice, discountPct: Number(i.discountPct),
  // A line with an article keeps the rate the server gave it; one without takes the general rate.
  vatPct: i.productId ? Number(i.vatPct) : null,
}));

const money = (n: number | null | undefined, cur = "EUR") =>
  formatMoney(n, cur);

const today = () => new Date().toISOString().slice(0, 10);

// Net-payment ("Valuta") day presets; free numeric entry is also allowed.
const PAYMENT_DAY_PRESETS = [15, 30, 60, 120];
const DEFAULT_PAYMENT_DAYS = "30";

export default function OfferEditorPage() {
  const { canWrite: canWriteSection } = useSectionAccess("offers");
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const confirm = useConfirm();

  const [offer, setOffer] = useState<Offer | null>(null);
  // "Pošalji kupcu" — the mail modal with this offer preselected as the PDF attachment.
  const [mailOpen, setMailOpen] = useState(false);
  // "Was this offer already mailed?" — the marker next to the send button.
  const [mailSends, setMailSends] = useState<Communication[]>([]);
  const [stages, setStages] = useState<OfferStage[]>([]);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState("");
  const [addr1, setAddr1] = useState("");
  const [addr2, setAddr2] = useState("");
  const [city, setCity] = useState("");
  const [postal, setPostal] = useState("");
  const [country, setCountry] = useState("");
  const [deliveryTerms, setDeliveryTerms] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  // Editable expiry; empty on a new offer = server default (offer date + 7 days).
  const [validUntil, setValidUntil] = useState("");
  const [vatEnabled, setVatEnabled] = useState(false);
  const [docLang, setDocLang] = useState<OfferDocLanguage>("SR");
  const [vatNumber, setVatNumber] = useState("");
  const [paymentTerms, setPaymentTerms] = useState<string>("BY_AGREEMENT");
  const [paymentNote, setPaymentNote] = useState("");
  const [paymentDays, setPaymentDays] = useState<string>(DEFAULT_PAYMENT_DAYS);
  const [prevoz, setPrevoz] = useState<string>("");
  // Who pays the transport: Bankom by default (its own cost, unseen by the customer), or the customer (a line of the offer).
  const [transportPaidBy, setTransportPaidBy] = useState<"BANKOM" | "CUSTOMER">("BANKOM");
  // The incoterm and its place from the ERP's lists, as the ERP's pro forma invoice carries them; the order made
  // from the offer takes them over, and its approval requires them.
  const [incoterm, setIncoterm] = useState("");
  const [incotermPlace, setIncotermPlace] = useState("");
  const [erpLists, setErpLists] = useState<ContractErpTerms>({ incoterms: [], paymentTerms: [], deliveryPlaces: [] });
  // The customer's contracts in force and the one the offer is made under ("" = none). One in force is taken on a
  // new offer by itself; with several the rep picks, as the same article often sits in more than one.
  const [contracts, setContracts] = useState<Contract[]>([]);
  // Today's NBS middle rate for each currency the customer's contracts are in, for a domestic offer's dinar prices.
  const [dinarRates, setDinarRates] = useState<Record<string, DinarRate | null>>({});
  const [contractId, setContractId] = useState("");
  const [contractChosen, setContractChosen] = useState(false);
  const [items, setItems] = useState<EditItem[]>([]);
  const [busy, setBusy] = useState(false);
  // Orders already standing on this offer. null = the answer is not in; `madeChecking` tells
  // the two apart (still reading vs. the read failed).
  const [madeOrders, setMadeOrders] = useState<MadeOrder[] | null>(null);
  const [madeChecking, setMadeChecking] = useState(false);

  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState<CustomerSuggestion[]>([]);
  const [prodQuery, setProdQuery] = useState("");
  const [prodResults, setProdResults] = useState<ProductSummary[]>([]);

  // An offer from the ERP is shown here and changed only there: no edits and no stage moves.
  // An order may still be made from it, and it may still be mailed to the customer.
  const fromErp = !!offer?.fromErp;
  const canWrite = canWriteSection && !fromErp;

  useEffect(() => {
    if (isNew || !id) return;
    // Has this offer already gone out by mail? Drives the marker in the header.
    void mailApi.timeline({ offerId: id }).then(setMailSends).catch(() => setMailSends([]));
    offersApi.get(id).then((o) => {
      setOffer(o);
      setCompanyId(o.companyId);
      setCustomerName(o.customerName);
      setAddr1(o.customerAddress1 ?? ""); setAddr2(o.customerAddress2 ?? "");
      setCity(o.customerCity ?? ""); setPostal(o.customerPostalCode ?? ""); setCountry(o.customerCountry ?? "");
      setDeliveryTerms(o.deliveryTerms ?? ""); setDeliveryDate(o.deliveryDate ?? "");
      setValidUntil(o.validUntil ?? "");
      setVatEnabled(o.vatEnabled); setDocLang(o.documentLanguage);
      setVatNumber(o.customerVatNumber ?? "");
      setPaymentTerms(o.paymentTerms ?? "BY_AGREEMENT"); setPaymentNote(o.paymentTermsNote ?? "");
      setPaymentDays(o.paymentTermsDays != null ? String(o.paymentTermsDays) : DEFAULT_PAYMENT_DAYS);
      setPrevoz(o.transportAmount != null ? String(o.transportAmount) : "");
      setTransportPaidBy(o.transportPaidBy === "CUSTOMER" ? "CUSTOMER" : "BANKOM");
      setIncoterm(o.incoterm ?? ""); setIncotermPlace(o.incotermPlace ?? "");
      setContractId(o.contractId ?? ""); setContractChosen(true);
      setItems(toEditItems(o));
    }).catch((e: ApiError) => {
      // Another owner's offer 404s (ownership hides it, same as a missing id) —
      // send the user to /forbidden instead of falling through to a blank "new offer" form.
      if (e.status === 404 || e.status === 403) { navigate("/forbidden", { replace: true }); return; }
      toast.error(e.message);
    });
  }, [id, isNew, toast, navigate]);

  // Prefill a brand-new offer from query params — used by the Lidovi convert
  // flow's "Napravi ponudu" shortcut (/offers/new?companyId=…&customerName=…).
  // Only on a new offer; an existing offer's own load wins.
  useEffect(() => {
    ordersApi.erpTerms().then(setErpLists).catch(() => undefined);
  }, []);
  useEffect(() => {
    if (!isNew) return;
    const cid = searchParams.get("companyId");
    const cname = searchParams.get("customerName");
    if (cid) setCompanyId(cid);
    if (cname) setCustomerName(cname);
  }, [isNew, searchParams]);

  // Did an order already come out of this offer? Read before the action is offered, so the
  // screen can name the order that exists instead of quietly making a second one. Only the
  // desk that may create an order asks the question.
  const canCreateOrder = canWriteSection && hasPermission("section:orders:write") && hasPermission("orders:create");
  useEffect(() => {
    if (isNew || !id || !canCreateOrder) return;
    let live = true;
    // Another offer's answer is not this one's: drop it before asking again.
    setMadeOrders(null);
    setMadeChecking(true);
    ordersApi.listFromOffer(id)
      .then((r) => {
        if (!live) return;
        setMadeOrders(r.map((o) => ({ id: o.id, orderNumber: o.orderNumber, status: o.status })));
      })
      // A failed check is not an empty answer: leaving it null makes the action ask first.
      .catch(() => { if (live) setMadeOrders(null); })
      .finally(() => { if (live) setMadeChecking(false); });
    return () => { live = false; };
  }, [id, isNew, canCreateOrder]);

  // Pipeline stages, passed down to OfferPipelineHeader (stepper + due/tags
  // pickers + notes/activity tabs) below.
  useEffect(() => {
    offerStagesApi.list().then(setStages).catch(() => setStages([]));
  }, []);

  // customer autofill search
  useEffect(() => {
    if (custQuery.trim().length < 2) { setCustResults([]); return; }
    let live = true;
    const t = setTimeout(() => {
      offersApi.suggestCustomers(custQuery.trim()).then((r) => { if (live) setCustResults(r); }).catch(() => undefined);
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [custQuery]);

  // Product search for adding items, named in the offer's own language: the line keeps the name it is added with, and
  // the document and its mail print that name.
  useEffect(() => {
    if (prodQuery.trim().length < 2) { setProdResults([]); return; }
    let live = true;
    const t = setTimeout(() => {
      catalogApi.searchProducts(prodQuery.trim(), docLang.toLowerCase(), 25).then((r) => { if (live) setProdResults(r); }).catch(() => undefined);
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [prodQuery, docLang]);

  // The customer's country decides VAT: a domestic customer is charged it, a foreign one (export) is not. The rep may
  // still change it, for a domestic customer in a free zone or a foreign one taking the goods in Serbia.
  const chooseCountry = (c: string) => {
    setCountry(c);
    if (c) setVatEnabled(isDomestic(c));
  };

  const pickCustomer = (c: CustomerSuggestion) => {
    setCompanyId(c.companyId); setCustomerName(c.name);
    setAddr1(c.address1 ?? ""); setAddr2(c.address2 ?? "");
    setCity(c.city ?? ""); setPostal(c.postalCode ?? ""); chooseCountry(c.country ?? "");
    setVatNumber(c.vatNumber ?? "");
    setCustQuery(""); setCustResults([]);
  };

  // A domestic offer is in dinars, whatever its contract's currency: domestic trade is in dinars by law. Otherwise the
  // contract's currency is the offer's, and without one a saved offer keeps its own and a new one takes it from the
  // customer's country, as the server will.
  const contract = contracts.find((c) => c.id === contractId) ?? null;
  const cur = isDomestic(country)
    ? offer?.currency ?? "RSD"
    : contract?.currency ?? offer?.currency ?? offerCurrencyForCountry(country);
  const contractRate = contract?.currency ? dinarRates[contract.currency] ?? null : null;
  const contractPrice = (c: Contract | null, productId: string | null) => {
    const agreed = c && productId ? c.lines.find((l) => l.productId === productId && l.unitPrice != null)?.unitPrice ?? null : null;
    const rate = c?.currency ? dinarRates[c.currency] : null;
    return contractPriceIn(agreed, c?.currency ?? null, cur, rate?.dinars ?? null);
  };

  const addProduct = (p: ProductSummary) => {
    const line = offerLineFromProduct(p, cur);
    const agreed = contractPrice(contract, p.id);
    setItems((prev) => [...prev, agreed != null ? { ...line, unitPrice: agreed } : line]);
    setProdQuery(""); setProdResults([]);
  };

  /** Make the offer under a contract, or under none: its incoterm, place, payment and prices fill the offer. */
  const chooseContract = (c: Contract | null) => {
    setContractId(c?.id ?? "");
    if (!c) return;
    if (c.incoterm) setIncoterm(c.incoterm);
    if (c.incotermPlace) setIncotermPlace(c.incotermPlace);
    if (c.paymentTerms) {
      // The contract's term as the offer's own option: an advance is an advance, days are deferred payment.
      const payment = offerPaymentFromErp(c.paymentTerms);
      setPaymentTerms(payment.terms);
      if (payment.days != null) setPaymentDays(String(payment.days));
      setPaymentNote(payment.note ?? "");
    }
    setItems((prev) => prev.map((it) => {
      const agreed = contractPrice(c, it.productId);
      return agreed != null ? { ...it, unitPrice: agreed } : it;
    }));
  };

  // The customer's contracts in force today; a new offer takes the only one by itself.
  const savedContractId = offer && offer.companyId === companyId ? offer.contractId ?? null : null;
  useEffect(() => {
    if (!companyId) { setContracts([]); return; }
    let live = true;
    const today = new Date().toISOString().slice(0, 10);
    contractsApi.list({ companyId, status: "ACTIVE" })
      .then(async (list) => {
        let valid = list.filter((c) => (!c.effectiveDate || c.effectiveDate <= today) && (!c.expiresAt || c.expiresAt >= today));
        // A saved offer keeps the contract it was made under after that contract ends, so it is still offered here.
        if (savedContractId && !valid.some((c) => c.id === savedContractId)) {
          const own = await contractsApi.get(savedContractId).catch(() => null);
          if (own) valid = [own, ...valid];
        }
        // Before the contracts, so a contract taken by itself already has the rate its prices convert at.
        const foreign = [...new Set(valid.map((c) => c.currency).filter((c): c is string => !!c && c !== "RSD"))];
        const rates = await Promise.all(foreign.map((c) => offersApi.dinarRate(c).catch(() => null)));
        if (!live) return;
        setDinarRates(Object.fromEntries(foreign.map((c, i) => [c, rates[i]])));
        setContracts(valid);
        // Another customer's contract does not follow the offer to this one.
        setContractId((prev) => (prev && !valid.some((c) => c.id === prev) ? "" : prev));
      })
      .catch(() => { if (live) setContracts([]); });
    return () => { live = false; };
  }, [companyId, savedContractId]);
  useEffect(() => {
    if (contractChosen || contractId || contracts.length !== 1) return;
    chooseContract(contracts[0]);
    // chooseContract reads only setters and the contract given; the rule runs when the contracts arrive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contracts, contractChosen, contractId]);

  const setItem = (idx: number, patch: Partial<EditItem>) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx: number) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const lineTotal = (it: EditItem) =>
    it.unitPrice == null ? null : Math.round(it.unitPrice * (1 - it.discountPct / 100) * it.quantity * 100) / 100;
  // The transport the customer pays is a line of the offer and in its total, as on its document; Bankom's is neither.
  const customerTransport = transportPaidBy === "CUSTOMER" && Number(prevoz) > 0 ? Math.round(Number(prevoz) * 100) / 100 : 0;
  const net = useMemo(() => items.reduce((s, it) => s + (lineTotal(it) ?? 0), 0), [items]) + customerTransport;
  const pdv = offerVat([
    ...items.map((it) => ({ net: lineTotal(it) ?? 0, vatPct: it.vatPct })),
    ...(customerTransport ? [{ net: customerTransport, vatPct: GENERAL_VAT }] : []),
  ]);
  const vatAmount = vatEnabled ? pdv.amount : 0;
  const grand = Math.round((net + vatAmount) * 100) / 100;

  async function save() {
    if (!canWrite) return;
    if (!customerName.trim()) { toast.error(t("offers.toast.customerRequired")); return; }
    const body = {
      companyId, customerName: customerName.trim(),
      customerAddress1: addr1 || null, customerAddress2: addr2 || null,
      customerCity: city || null, customerPostalCode: postal || null, customerCountry: country || null,
      customerVatNumber: vatNumber || null,
      validUntil: validUntil || null,
      deliveryTerms: deliveryTerms || null, deliveryDate: deliveryDate || null,
      incoterm, incotermPlace, contractId: contractId || null,
      transportPaidBy,
      paymentTerms, paymentTermsNote: paymentTerms === "OTHER" ? (paymentNote || null) : null,
      paymentTermsDays: paymentTerms === "VALUTA" ? (paymentDays === "" ? null : Number(paymentDays)) : null,
      // The delivery date is the only delivery term: a lead time in weeks beside it said the same thing twice.
      deliveryWeeks: null,
      transportAmount: prevoz === "" ? null : Number(prevoz),
      vatEnabled, documentLanguage: docLang,
      items: items.map((it) => ({
        productId: it.productId, sku: it.sku, name: it.name, quantity: it.quantity,
        unit: it.unit, unitPrice: it.unitPrice, discountPct: it.discountPct,
      })),
    };
    setBusy(true);
    try {
      const saved = isNew ? await offersApi.create(body) : await offersApi.update(id!, body);
      toast.success(t("offers.toast.saved", { number: saved.offerNumber }));
      navigate(`/offers/${saved.id}`, { replace: true });
      setOffer(saved);
      // The server may have filled a price the line was sent without; show what was stored.
      setItems(toEditItems(saved));
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Turns this offer into an order and goes to it. The order starts as a draft, carries the
   * prices the customer accepted and the link back to the offer, and the offer moves to the won
   * stage. Whoever makes it cannot approve it — approval is the step that hands the order to the
   * ERP and it needs a second signature.
   *
   * A second order from one offer stays possible (a customer who accepts a quote and calls the
   * goods off in parts), so the screen asks instead of hiding the action.
   */
  async function makeOrder() {
    // The screen goes busy BEFORE the question, not after it: the dialog provider queues requests,
    // so a button left enabled while a question is on screen turns two clicks into two questions and
    // two orders. Everything below runs inside the try, so an answered "no" releases the screen too.
    if (!canWriteSection || !offer || busy) return;
    setBusy(true);
    try {
      const action = toOrderAction(offer, canCreateOrder, madeOrders, madeChecking);
      if (action.kind === "again") {
        const ok = await confirm({
          message: t(
            "offers.editor.toOrder.againConfirm",
            "Od ove ponude je već nastala porudžbina {{numbers}}. Napraviti još jednu?",
            { numbers: madeOrderNumbers(action.made) },
          ),
          confirmLabel: t("offers.editor.toOrder.againConfirmLabel", "Napravi još jednu"),
        });
        if (!ok) return;
      } else if (action.kind === "unknown") {
        const ok = await confirm({
          message: t(
            "offers.editor.toOrder.unknownConfirm",
            "Provera da li od ove ponude već postoji porudžbina nije uspela. Napraviti porudžbinu?",
          ),
        });
        if (!ok) return;
      } else if (action.kind !== "create") {
        return;
      }
      const order = await ordersApi.createFromOffer(offer.id);
      toast.success(t("offers.editor.toOrder.created", "Porudžbina {{number}} je napravljena.", {
        number: order.orderNumber,
      }));
      navigate(`/orders/${order.id}`);
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function downloadDoc(kind: "pdf" | "xlsx") {
    if (!offer) return;
    try {
      const res = await fetch(`/api/offers/${offer.id}.${kind}`, { credentials: "include" });
      if (!res.ok) { toast.error(t("offers.toast.downloadFailed")); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Ponuda_${offer.offerNumber}.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("offers.toast.downloadFailed"));
    }
  }

  const orderAction = toOrderAction(offer, canCreateOrder, madeOrders, madeChecking);

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label={t("offerEditor.breadcrumb.aria", "Breadcrumb")}>
            <span>{t("offers.crumb.commercial")}</span>
            <span><Link to="/offers">{t("offers.list.title")}</Link></span>
            <span>{isNew ? t("offers.list.new") : offer?.offerNumber ?? "…"}</span>
          </nav>
          <h1>{isNew ? t("offers.list.new") : offer?.offerNumber ?? t("offers.list.title")}</h1>
          <div className="sub">
            {offer
              ? `${offer.ownerName ?? "—"}${offer.createdAt ? ` · ${t("offers.editor.created", { date: formatDate(offer.createdAt) })}` : ""}`
              : t("offers.editor.subtitleDraft")}
          </div>
        </div>
        <div className="page-actions">
          {mailSends.length > 0 && (
            <span
              className="chip"
              style={{ borderColor: "var(--ok)", color: "var(--ok)" }}
              title={mailSends
                .map((m) => formatDateTime(m.sentAt ?? m.createdAt))
                .join("\n")}
            >
              <Ic.check />
              {mailSends.length === 1
                ? t("mail.send.offerSentOnce", {
                    when: formatDate(mailSends[0].sentAt ?? mailSends[0].createdAt),
                    defaultValue: "Mailed {{when}}",
                  })
                : t("mail.send.offerSentTimes", {
                    count: mailSends.length,
                    defaultValue: "Mailed {{count}}×",
                  })}
            </span>
          )}
          <button type="button" className="btn" onClick={() => navigate("/offers")}>{t("action.back")}</button>
          {offer && <button type="button" className="btn" onClick={() => downloadDoc("pdf")}><Ic.download /> PDF</button>}
          {offer && <button type="button" className="btn" onClick={() => downloadDoc("xlsx")}><Ic.download /> Excel</button>}
          {offer && canWriteSection && hasPermission("section:personal_mail:write") && hasPermission("mail:connect") && (
            <button
              type="button"
              className="btn"
              onClick={() => setMailOpen(true)}
              // A message must hang off a lead or a customer company; an offer
              // typed for a free-text customer has neither, so there is nothing
              // to file the correspondence under.
              disabled={!offer.companyId}
              title={offer.companyId ? undefined : t("mail.send.offerNoCompany", "Link this offer to a customer company first.")}
            >
              <Ic.send /> {t("mail.send.toCustomer", "Send to customer")}
            </button>
          )}
          {orderAction.kind === "again" && orderAction.made.map((o) => (
            <Link
              key={o.id}
              to={`/orders/${o.id}`}
              className="chip"
              style={{ textDecoration: "none" }}
              title={t("offers.editor.toOrder.madeTitle", "Otvori porudžbinu nastalu od ove ponude")}
            >
              <Ic.commercial />
              {t("offers.editor.toOrder.made", "Porudžbina {{number}}", { number: o.orderNumber })}
            </Link>
          ))}
          {orderAction.kind !== "none" && (
            <button
              type="button"
              className="btn"
              disabled={busy || !toOrderClickable(orderAction)}
              title={orderAction.kind === "blocked"
                ? t("offers.editor.toOrder.noCompany", "Ponuda prvo mora da nosi kupca iz baze kupaca.")
                : undefined}
              onClick={makeOrder}
            >
              <Ic.arrowRight />
              {orderAction.kind === "again"
                ? t("offers.editor.toOrder.actionAgain", "Još jedna porudžbina")
                : t("offers.editor.toOrder.action", "Napravi porudžbinu")}
            </button>
          )}
          <button type="button" disabled={busy || !canWrite} className="btn primary" onClick={save}><Ic.check /> {t("action.save")}</button>
        </div>
      </div>

      {fromErp && <ErpReadOnlyNote text={t("offers.editor.fromErp")} />}
      {offer && (offer.previousOfferNumber || (offer.laterOffers?.length ?? 0) > 0) && (
        <p style={{ margin: 0, fontSize: 13, color: "var(--ink-3)" }}>
          {offer.previousOfferNumber && offer.previousOfferId && (
            <>{t("offers.previousOffer", "Nastala iz ponude")} <Link to={`/offers/${offer.previousOfferId}`}>{offer.previousOfferNumber}</Link></>
          )}
          {offer.previousOfferNumber && (offer.laterOffers?.length ?? 0) > 0 && " · "}
          {(offer.laterOffers?.length ?? 0) > 0 && (
            <>{t("offers.laterOffers", "Iz nje nastale")}{" "}
              {offer.laterOffers!.map((n, i) => (
                <span key={n.id}>{i > 0 && ", "}<Link to={`/offers/${n.id}`}>{n.offerNumber}</Link></span>
              ))}
            </>
          )}
        </p>
      )}

      {offer && (
        <OfferPipelineHeader offer={offer} stages={stages} onOfferChange={setOffer} stageLocked={fromErp} />
      )}

      <fieldset disabled={!canWrite} style={{ border: 0, margin: 0, padding: 0, minWidth: 0, display: "contents" }}>
      <div style={kpiStrip}>
        <div style={kpiTile}>
          <span style={kpiLabel}>{t("offers.kpi.items")}</span>
          <strong style={kpiValueLg}>{items.length}</strong>
        </div>
        <div style={kpiTile}>
          <span style={kpiLabel}>{t("offers.kpi.net")}</span>
          <strong style={kpiValueLg}>{money(net, cur)}</strong>
        </div>
        <div style={kpiTile}>
          <span style={kpiLabel}>{t("offers.kpi.vat")}</span>
          <strong style={kpiValueLg}>{vatEnabled ? money(vatAmount, cur) : <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>—</span>}</strong>
        </div>
        <div style={kpiTile}>
          <span style={kpiLabel}>{t("offers.col.total")}</span>
          <strong style={kpiValueLg}>{money(grand, cur)}</strong>
        </div>
      </div>

      <section style={card}>
        <h2 style={cardTitle}>{t("offers.col.customer")}</h2>
        <div style={{ position: "relative", maxWidth: 520, marginTop: 4 }}>
          <input
            value={customerName}
            onChange={(e) => { setCustomerName(e.target.value); setCompanyId(null); setCustQuery(e.target.value); }}
            placeholder={t("offers.editor.customerPlaceholder")}
            className="input"
          />
          {custResults.length > 0 && (
            <div className="dd-scroll" style={typeaheadPanel}>
              {custResults.map((c) => (
                <button type="button" key={c.companyId} style={typeaheadOption} onClick={() => pickCustomer(c)}>
                  <span style={{ flex: 1 }}>{c.name}</span>
                  <span style={{ color: "var(--ink-3)", fontSize: 12 }}>{c.city ?? ""}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div style={fieldGrid}>
          <Field label={t("offers.field.address1")}><input value={addr1} onChange={(e) => setAddr1(e.target.value)} className="input" /></Field>
          <Field label={t("offers.field.address2")}><input value={addr2} onChange={(e) => setAddr2(e.target.value)} className="input" /></Field>
          <Field label={t("offers.field.city")}><input value={city} onChange={(e) => setCity(e.target.value)} className="input" /></Field>
          <Field label={t("offers.field.postalCode")}><input value={postal} onChange={(e) => setPostal(e.target.value)} className="input" /></Field>
          <Field label={t("offers.field.country")}>
            <CountrySelect value={country} onChange={chooseCountry} />
          </Field>
          <Field label={t("offers.field.vatNumber")}><input value={vatNumber} onChange={(e) => setVatNumber(e.target.value)} className="input" /></Field>
        </div>
      </section>

      <section style={card}>
        <h2 style={cardTitle}>{t("offers.section.terms")}</h2>
        <div style={fieldGrid}>
          <Field label={t("offers.col.date")}>
            <input value={formatDate(offer?.offerDate ?? today())} disabled className="input" style={{ background: "var(--surface-2)" }} />
          </Field>
          <Field label={t("offers.field.validUntil", "Valid until")}>
            <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="input" />
          </Field>
          {contracts.length > 0 && (
            <Field label={t("offers.field.contract", "Contract")}>
              <Select<string>
                value={contractId}
                onChange={(value) => { setContractChosen(true); chooseContract(contracts.find((c) => c.id === value) ?? null); }}
                ariaLabel={t("offers.field.contract", "Contract")}
                options={[
                  { value: "", label: t("offers.field.contractNone", "No contract") },
                  ...contracts.map((c) => ({
                    value: c.id,
                    label: `${c.erpDocumentNumber || c.referenceNumber}${c.expiresAt ? ` · ${t("offers.field.contractUntil", "until")} ${formatDate(c.expiresAt)}` : ""}${c.incoterm ? ` · ${c.incoterm}${c.incotermPlace ? ` ${c.incotermPlace}` : ""}` : ""}`,
                  })),
                ]}
              />
              {contracts.length > 1 && !contractId && (
                <span style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 4, display: "block" }}>
                  {t("offers.field.contractPick", "The customer has several contracts in force: pick the one this offer is under.")}
                </span>
              )}
              {contract?.currency && contract.currency !== "RSD" && cur === "RSD" && (
                <span style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 4, display: "block" }}>
                  {contractRate
                    ? t("offers.field.contractRate", {
                        currency: contract.currency,
                        rate: formatNumber(contractRate.dinars, undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 }),
                        day: formatDate(contractRate.day),
                        defaultValue: "The contract is in {{currency}} and a domestic offer in dinars: its prices at the NBS middle rate of {{day}}, 1 {{currency}} = {{rate}} RSD.",
                      })
                    : t("offers.field.contractNoRate", {
                        currency: contract.currency,
                        defaultValue: "The contract is in {{currency}} and there is no NBS rate for today: type the prices in dinars.",
                      })}
                </span>
              )}
            </Field>
          )}
          {/* The incoterm and its place from the ERP's lists replace the free text of the delivery terms; an older
              offer's text still prints on its document until an incoterm is picked. */}
          <Field label={t("offers.field.incoterm", "Incoterm")}>
            <Select<string>
              value={incoterm}
              onChange={setIncoterm}
              ariaLabel={t("offers.field.incoterm", "Incoterm")}
              placeholder={t("orderErp.incotermPick", "Pick an incoterm from the ERP")}
              options={[
                { value: "", label: "—" },
                ...(incoterm && !erpLists.incoterms.some((i) => i.code === incoterm) ? [{ value: incoterm, label: incoterm }] : []),
                ...erpLists.incoterms.map((i) => ({ value: i.code, label: `${i.code} — ${i.name}` })),
              ]}
            />
          </Field>
          <Field label={t("offers.field.incotermPlace", "Place of delivery")}>
            <input
              value={incotermPlace}
              onChange={(e) => setIncotermPlace(e.target.value)}
              maxLength={50}
              list="offer-erp-places"
              placeholder={t("offers.field.incotermPlacePick", "Pick a place from the ERP or type a new one")}
              className="input"
            />
            <datalist id="offer-erp-places">
              {erpLists.deliveryPlaces.map((m) => <option key={m} value={m} />)}
            </datalist>
          </Field>
          <Field label={t("offers.field.deliveryDate")}>
            <input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="input" />
          </Field>
          <Field label={t("offers.kpi.vat")}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, height: 36 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--ink-2)" }}>
                <input type="checkbox" checked={vatEnabled} onChange={(e) => setVatEnabled(e.target.checked)} />
                {t("offers.field.vatApply")}
              </label>
            </div>
          </Field>
          <Field label={t("offers.field.docLang")}>
            <Select<OfferDocLanguage>
              value={docLang}
              onChange={setDocLang}
              ariaLabel={t("offers.field.docLang")}
              options={SUPPORTED_LOCALES.map((l) => ({
                value: l.toUpperCase() as OfferDocLanguage,
                label: localeLabel(l),
                left: <span style={{ fontSize: 14, lineHeight: 1 }}>{localeFlag(l)}</span>,
              }))}
            />
          </Field>
          <Field label={t("offers.field.paymentTerms")}>
            <Select<string>
              value={paymentTerms}
              onChange={setPaymentTerms}
              ariaLabel={t("offers.field.paymentTerms")}
              options={[
                { value: "ADVANCE_PAYMENT", label: t("offers.payment.advance") },
                { value: "BY_AGREEMENT", label: t("offers.payment.byAgreement") },
                { value: "VALUTA", label: t("offers.payment.valuta") },
                { value: "OTHER", label: t("offers.payment.other") },
              ]}
            />
            {paymentTerms === "OTHER" && (
              <input
                value={paymentNote}
                onChange={(e) => setPaymentNote(e.target.value)}
                placeholder={t("offers.field.paymentNote")}
                className="input"
                style={{ marginTop: 6 }}
              />
            )}
            {paymentTerms === "VALUTA" && (
              <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {PAYMENT_DAY_PRESETS.map((d) => (
                    <button
                      type="button"
                      key={d}
                      className={paymentDays === String(d) ? "btn sm primary" : "btn sm"}
                      aria-pressed={paymentDays === String(d)}
                      onClick={() => setPaymentDays(String(d))}
                    >
                      {d}
                    </button>
                  ))}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="number" min={0} step={1} value={paymentDays}
                    onChange={(e) => setPaymentDays(e.target.value)}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="input" style={{ width: 100, textAlign: "right" }}
                    aria-label={t("offers.field.paymentDays")}
                  />
                  <span style={{ color: "var(--ink-3)", fontSize: 13 }}>{t("offers.payment.daysUnit")}</span>
                </div>
              </div>
            )}
          </Field>
          {/* Bankom's transport is its own cost, which the ERP spreads over the lines and the customer never sees; the
              customer's is a line of the offer, in its total, and of the ERP pro forma invoice. */}
          <Field label={t("offers.field.transport", "Transport")}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="number" min={0} step="0.01" value={prevoz}
                onChange={(e) => setPrevoz(e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                className="input" style={{ width: 140, textAlign: "right" }}
                aria-label={t("offers.field.transport", "Transport")}
              />
              <span style={{ color: "var(--ink-3)", fontSize: 13, whiteSpace: "nowrap" }}>
                {cur}, {t("offers.field.withoutVat", "without VAT")}
              </span>
              <div style={{ minWidth: 220, flex: 1 }}>
                <Select<"BANKOM" | "CUSTOMER">
                  value={transportPaidBy}
                  onChange={setTransportPaidBy}
                  ariaLabel={t("offers.field.transportPaidBy", "Who pays the transport")}
                  options={[
                    { value: "BANKOM", label: t("offers.transport.bankom", "Bankom's cost, not shown to the customer") },
                    { value: "CUSTOMER", label: t("offers.transport.customer", "The customer's cost, a line of the offer") },
                  ]}
                />
              </div>
            </div>
          </Field>
        </div>
      </section>

      <section style={card}>
        <header style={cardHead}>
          <h2 style={cardTitle}>{t("offers.section.items")}</h2>
          <span style={{ color: "var(--ink-3)", fontSize: 12 }}>{t("offers.items.lines", { n: items.length })}</span>
        </header>

        {items.length === 0 ? (
          <div style={emptyState}>
            <p style={{ margin: 0, color: "var(--ink-3)" }}>{t("offers.items.empty.title")}</p>
            <p style={{ margin: "6px 0 0", color: "var(--ink-4)", fontSize: 12 }}>{t("offers.items.empty.hint")}</p>
          </div>
        ) : (
          <table className="tbl" style={tbl}>
            <thead>
              <tr>
                <th style={th}>#</th>
                <th style={th}>{t("offers.items.col.code")}</th>
                <th style={th}>{t("offers.items.col.item")}</th>
                <th style={{ ...th, textAlign: "right" }}>{t("offers.items.col.qty")}</th>
                <th style={{ ...th, textAlign: "right" }}>{t("offers.items.col.price")} ({cur})</th>
                <th style={{ ...th, textAlign: "right" }}>{t("offers.items.col.disc")}</th>
                <th style={{ ...th, textAlign: "right" }}>{t("offers.items.col.vat", "VAT")}</th>
                <th style={{ ...th, textAlign: "right" }}>{t("offers.items.col.lineTotal")}</th>
                <th style={{ ...th, width: 48 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i}>
                  <td style={td}>{i + 1}</td>
                  <td style={td}>{it.sku ? <span style={mono}>{it.sku}</span> : "—"}</td>
                  <td style={td}><strong style={{ fontWeight: 500 }}>{it.name}</strong></td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6 }}>
                      <QtyStepper
                        value={it.quantity}
                        min={0}
                        step={1}
                        ariaLabel={t("offers.items.col.qty")}
                        onChange={(n) => setItem(i, { quantity: n })}
                      />
                      <span style={{ color: "var(--ink-3)", fontSize: 12, minWidth: 24, textAlign: "left" }}>
                        {isPieces(it.unit) ? t("catalog.pdp.unit.pcs") : it.unit}
                      </span>
                    </div>
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <input type="number" min={0} step="0.01" value={it.unitPrice ?? ""}
                      onChange={(e) => setItem(i, { unitPrice: e.target.value === "" ? null : Number(e.target.value) })}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="input" style={{ width: 104, textAlign: "right" }} />
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <input type="number" min={0} max={100} step="0.5" value={it.discountPct}
                      onChange={(e) => setItem(i, { discountPct: Number(e.target.value) })}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="input" style={{ width: 72, textAlign: "right" }} />
                  </td>
                  <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", color: vatEnabled ? undefined : "var(--ink-3)" }}>
                    {`${it.vatPct ?? GENERAL_VAT}%`}
                  </td>
                  <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(lineTotal(it), cur)}</td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <button type="button" className="btn ghost sm" style={{ color: "var(--err)" }} aria-label={t("offerEditor.removeItem", "Remove item")} onClick={() => removeItem(i)}>
                      <Ic.x />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div style={addLineBlock}>
          <h3 style={addLineTitle}>{t("offers.items.add")}</h3>
          <div style={{ position: "relative", maxWidth: 520 }}>
            <input value={prodQuery} onChange={(e) => setProdQuery(e.target.value)} placeholder={t("offers.items.searchPlaceholder")} className="input" />
            {prodResults.length > 0 && (
              <div className="dd-scroll" style={typeaheadPanel}>
                {prodResults.map((p) => (
                  <button type="button" key={p.id} style={typeaheadOption} onClick={() => addProduct(p)}>
                    <span style={mono}>{p.sku}</span>
                    <span style={{ flex: 1 }}>{p.name}</span>
                    <span style={{ color: "var(--ink-3)", fontSize: 12 }}>{money(p.listPrice, p.currency ?? "EUR")}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <table style={{ fontSize: 13, borderCollapse: "collapse", minWidth: 240 }}>
            <tbody>
              <tr>
                <td style={sumLabel}>{t("offers.summary.net")}</td>
                <td style={sumValue}>{money(net, cur)}</td>
              </tr>
              <tr>
                <td style={sumLabel}>{t("offers.summary.vat")}{vatEnabled && pdv.rates.length ? ` (${pdv.rates.map((s) => `${s}%`).join(" / ")})` : ""}</td>
                <td style={sumValue}>{money(vatAmount, cur)}</td>
              </tr>
              <tr>
                <td style={{ ...sumLabel, paddingTop: 8, fontWeight: 700, color: "var(--ink)" }}>{t("offers.summary.total")}</td>
                <td style={{ ...sumValue, paddingTop: 8, fontWeight: 700, color: "var(--ink)" }}>{money(grand, cur)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      </fieldset>

      {canWriteSection && offer && offer.companyId && (
        <SendMailModal
          open={mailOpen}
          onClose={() => {
            setMailOpen(false);
            if (offer) void mailApi.timeline({ offerId: offer.id }).then(setMailSends).catch(() => {});
          }}
          customerCompanyId={offer.companyId}
          offer={{ id: offer.id, offerNumber: offer.offerNumber }}
          offerCompanyId={offer.companyId}
          defaultSubject={t("mail.send.offerSubject", { number: offer.offerNumber, defaultValue: "Offer {{number}}" })}
        />
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

const card: CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-md)",
  boxShadow: "var(--shadow-1)",
  padding: 18,
  display: "flex",
  flexDirection: "column",
  gap: 10,
};
const cardHead: CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 0 };
const cardTitle: CSSProperties = { margin: 0, fontSize: 14, fontWeight: 600, color: "var(--ink)" };
const fieldGrid: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 12, marginTop: 4 };
const fieldLabel: CSSProperties = {
  display: "block", fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4,
};
const tbl: CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 4 };
const th: CSSProperties = {
  textAlign: "left", padding: "10px 8px", color: "var(--ink-3)", fontWeight: 600,
  borderBottom: "1px solid var(--border)", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4,
};
const td: CSSProperties = { padding: "8px", borderBottom: "1px solid var(--border)", verticalAlign: "middle" };
const mono: CSSProperties = { fontFamily: "var(--mono)", fontSize: 11, color: "var(--ink-4)" };
const emptyState: CSSProperties = {
  padding: 24, textAlign: "center",
  background: "var(--surface-2)", border: "1px dashed var(--border-strong)", borderRadius: "var(--r-sm)",
};
const addLineBlock: CSSProperties = { marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--border)" };
const addLineTitle: CSSProperties = {
  margin: "0 0 8px", fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4,
};
const typeaheadPanel: CSSProperties = {
  position: "absolute", zIndex: 20, top: "calc(100% + 4px)", left: 0, right: 0,
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-sm)", boxShadow: "var(--shadow-2)", maxHeight: 320, overflowY: "auto",
};
const typeaheadOption: CSSProperties = {
  display: "flex", gap: 12, alignItems: "center",
  padding: "10px 12px", width: "100%", textAlign: "left",
  background: "transparent", border: "none", cursor: "pointer", fontSize: 13, color: "var(--ink)",
  borderBottom: "1px solid var(--border)",
};
const kpiStrip: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 };
const kpiTile: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-md)", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 4,
};
const kpiLabel: CSSProperties = { fontSize: 10, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--ink-3)" };
const kpiValueLg: CSSProperties = { fontSize: 18, color: "var(--ink)", display: "flex", alignItems: "center", gap: 6, fontVariantNumeric: "tabular-nums" };
const sumLabel: CSSProperties = { padding: "3px 16px 3px 0", color: "var(--ink-3)" };
const sumValue: CSSProperties = { padding: "3px 0", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--ink-2)" };
